import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";
import type { LinkProps } from "react-router-dom";

/**
 * 터치 타깃 안전 버튼 — 최소 크기는 밀도 토큰(--pf-touch-target: SE 64px · SU 44px,
 * size="remote" 는 --pf-touch-target-remote: SE 96px)에서 온다(DR-03 · SR-NFR-06).
 * SE 에서는 아이콘을 라벨 위에 크게 둔다(아이콘 우선 라벨). 아이콘은 장식이라 읽지 않는다.
 */
export type ButtonVariant = "primary" | "green" | "accent" | "ghost";
export type ButtonSize = "default" | "remote";

interface Common {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  block?: boolean;
}

function classes({ variant = "primary", size = "default", block }: Common, extra?: string) {
  return [
    "pf-btn",
    `pf-btn--${variant}`,
    size === "remote" && "pf-btn--remote",
    block && "pf-btn--block",
    extra,
  ]
    .filter(Boolean)
    .join(" ");
}

function Inner({ icon, children }: { icon?: ReactNode; children?: ReactNode }) {
  return (
    <>
      {icon != null && (
        <span className="pf-btn__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      {children != null && <span className="pf-btn__label">{children}</span>}
    </>
  );
}

export type ButtonProps = Common & ButtonHTMLAttributes<HTMLButtonElement>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, icon, block, className, children, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={classes({ variant, size, block }, className)}
      {...rest}
    >
      <Inner icon={icon}>{children}</Inner>
    </button>
  );
});

export type ButtonLinkProps = Common & LinkProps;

export function ButtonLink({
  variant,
  size,
  icon,
  block,
  className,
  children,
  ...rest
}: ButtonLinkProps) {
  return (
    <Link className={classes({ variant, size, block }, className)} {...rest}>
      <Inner icon={icon}>{children}</Inner>
    </Link>
  );
}
